import {
  Component,
  ViewChild,
  Inject,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  AfterViewInit
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { EndType } from '@cpd-app/core-module/models';
import { VideoUsageInput } from '@cpd-app/generated';
import { TrackerFacade } from '@cpd-app/state/tracker';
import {
  BitrateOptions,
  VgApiService,
  VgCoreModule
} from '@videogular/ngx-videogular/core';
import {
  VgHlsDirective,
  VgStreamingModule
} from '@videogular/ngx-videogular/streaming';
import { GuideDialogData } from '@cpd-app/core-module/models';
import { CommonModule } from '@angular/common';
import { DialogModule } from '../dialog/dialog.module';
import { VgBufferingModule } from '@videogular/ngx-videogular/buffering';
import { VgOverlayPlayModule } from '@videogular/ngx-videogular/overlay-play';
import { VgControlsModule } from '@videogular/ngx-videogular/controls';

@Component({
  selector: 'cpd-video-dialog',
  templateUrl: './video-dialog.component.html',
  styleUrls: ['./video-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    VgCoreModule,
    VgControlsModule,
    VgOverlayPlayModule,
    VgBufferingModule,
    VgStreamingModule
  ]
})
export class VideoDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(VgHlsDirective, { static: true }) public vgHls: VgHlsDirective;
  @ViewChild('media') public mediaContainer: ElementRef<HTMLVideoElement>;
  @ViewChild('mp4Video') public mp4VideoContainer: ElementRef<HTMLVideoElement>;

  public vgApi: VgApiService;
  public bitrates: BitrateOptions[];
  public mp4Video = false;

  private currentTime = 0;
  private dataTracker: VideoUsageInput = {
    url: '',
    duration: 0,
    spend: 0,
    endType: null
  };
  private videoElement: HTMLVideoElement;

  @HostListener('window:beforeunload', ['$event'])
  public onBeforeUnload(event: Event): void {
    event.preventDefault();
    this.dataTracker.endType = EndType.CLOSE_BROWSER;
    this.videoElement.pause();
    this.saveVideoUsage(EndType.CLOSE_BROWSER);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: GuideDialogData,
    public dialogRef: MatDialogRef<VideoDialogComponent>,
    private trackerFacade: TrackerFacade
  ) {
    this.mp4Video = data.videoResource?.includes('.mp4');
  }

  public ngOnInit(): void {
    this.dialogRef.disableClose = true;
    this.dialogRef.beforeClosed().subscribe(() => {
      this.saveVideoUsage(EndType.CLOSE_POPUP);
    });
  }

  public ngAfterViewInit(): void {
    this.trackMedia();
  }

  public ngOnDestroy(): void {
    if (!this.dataTracker.endType) {
      this.saveVideoUsage(EndType.LEAVE_PAGE);
    }
  }

  public onPlayerReady(api: VgApiService): void {
    this.vgApi = api;
  }

  public setBitrate(option: BitrateOptions): void {
    this.vgHls.setBitrate(option);
  }

  private trackMedia(): void {
    this.videoElement = this.mp4Video
      ? this.mp4VideoContainer.nativeElement
      : this.mediaContainer.nativeElement;

    this.dataTracker.url = this.data.videoResource;
    this.addEventListener();
  }

  private addEventListener() {
    this.videoElement.onloadeddata = () => {
      this.dataTracker.duration = Math.round(this.videoElement.duration);
    };

    this.videoElement.onplaying = (event) => {
      this.currentTime = Math.round(event.target['currentTime']);
    };

    this.videoElement.onpause = (event) => {
      this.updateWatchingTime(event);
    };

    this.videoElement.onended = (event) => {
      this.updateWatchingTime(event);
      this.saveVideoUsage(EndType.END_VIDEO);
    };
  }

  private updateWatchingTime(event: Event): void {
    const newTime = Math.round(event.target['currentTime']);
    const spendTime = newTime - this.currentTime;

    this.currentTime = newTime;
    this.dataTracker.spend += spendTime;

    this.trackerFacade.updateVideoUsage({ ...this.dataTracker });
  }

  private saveVideoUsage(endType: EndType): void {
    this.dataTracker.endType = endType;
    this.trackerFacade.updateVideoUsage({ ...this.dataTracker });

    if (this.dataTracker?.spend > 0) {
      this.trackerFacade.saveVideoUsage();

      this.dataTracker.spend = 0;
      this.dataTracker.endType = null;
    }
  }
}
